// ═══════════════════════════════════════════════════════════════════════════
// AI-callable safe-contour guard — DRAW-MODIFY domain (CC-2c sub-arm 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors the block-domain `assertAiCallableAction` (aiCallableGuard.ts): maps
// every draw-modify AI action to the user-facing registry command it targets,
// then reuses the CC-2a generic `assertAiCallableCommand` to fail CLOSED when
// that target is not `aiCallable: true` (or is absent from the registry).
//
// The guard is ACTION-level, not literal-level: the registry descriptor the
// action targets is checked (e.g. TRIM → "TRIM"), never the internal
// EditorCommand literal the translator emits (`geometry/trim-entity`). This is
// the same rule the block guard documents — the AI-callable surface is the
// command the AI is asking for.

import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
  type CommandRegistryId,
} from "@dxf-ai-editor/command-registry";

import { assertAiCallableCommand } from "./aiCallableGuard";
import type { DrawModifyAiCommand } from "./drawModifySchemas";

/**
 * Maps every draw-modify AI action to the registry command it targets. The
 * guard resolves this id and checks its `aiCallable` flag, so an action whose
 * target is ever flipped to `aiCallable: false` fails closed automatically.
 *
 * `Record<DrawModifyAiCommand["action"], …>` forces exhaustiveness: adding a
 * new draw-modify action without declaring its target command is a compile
 * error. Each target is `aiCallable: true` in the registry today and listed
 * AI-callable in ТЗ §10.1.
 */
export const DRAW_MODIFY_AI_ACTION_TARGET_COMMAND: Record<
  DrawModifyAiCommand["action"],
  CommandRegistryId
> = {
  TRIM: "TRIM",
  EXTEND: "EXTEND",
  OFFSET: "OFFSET",
  MIRROR: "MIRROR",
  SCALE: "SCALE",
  STRETCH: "STRETCH",
  ARRAYRECT: "ARRAYRECT",
  ARRAYEDIT: "ARRAYEDIT",
};

/**
 * Guard a single draw-modify AI action: resolves its target registry command
 * and asserts AI-callability. Throws `NonAiCallableCommandError` when the
 * target is not AI-callable (fail-closed). Called by
 * `translateDrawModifyCommand` before any EditorCommand is produced.
 */
export function assertAiCallableDrawAction(
  action: DrawModifyAiCommand["action"],
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): void {
  assertAiCallableCommand(
    DRAW_MODIFY_AI_ACTION_TARGET_COMMAND[action],
    descriptors,
  );
}
